import React, { useContext, useEffect, useRef, useState } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import PrimarySurvey from './PrimarySurvey/PrimarySurvey'
import BigModal from '../../../../components/Modals/BigModal'
import { EPCRContext } from '../EPCR'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import Respiratory from './Respiratory/Respiratory'
import moment from 'moment'
import DeleteIcon from '@mui/icons-material/Delete'
import Cardiovascular from './Cardiovascular/Cardiovascular'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NextButton from '../NextButton'
import AssessmentText from './AssessmentText'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import ECG from './ECG/ECG'
import Neuro from './Neurological/Neuro'
import Gastrointestinal from './Gastrointestinal/Gastrointestinal'
import Musculoskeletal from './Musculoskeletal/Musculoskeletal'
import HeadInjury from './Musculoskeletal/HeadInjury'
import Trauma from './Trauma/Trauma'
import Skin from './Skin/Skin'
import Wound from './Wound'
import POC from './POC/POC'
import BodyMaps from './BodyMaps/BodyMaps'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import MedicalDrawing from './BodyMaps/MedicalDrawing'
import ENT from './ENT/ENT'
import Safeguarding from '../Safeguarding/Safeguarding'
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import Eyes from './Eyes/Eyes'
import MentalHealth from './MentalHealth/MentalHealth'
import MentalCapacity from './MentalCapacity/MentalCapacity'
import Imaging from '../Imaging/Imaging'
import { ImpressionDialog } from '../Imaging/ImagingRequestsPanel'
import { syncImagingEntries } from '../Imaging/imagingAssessEntry'
import LiveDetailForm from '../liveCapture/LiveDetailForm'
import {
  LiveEntryActions,
  ManualInterventionButton,
} from '../liveCapture/LiveEntryControls'
import { isHiddenEntryKey } from '../liveCapture/liveUi'

const Assess = ({ handleChange, formValues }) => {
  const [selectedModule, setSelectedModule] = useState({})
  const [impressionRequest, setImpressionRequest] = useState(null)
  const [detailRequest, setDetailRequest] = useState(null)
  const isCriticalCare = formValues?.mode === 'Critical Care'
  const { formID } = useContext(EPCRContext)
  const imagingEnabled =
    useContext(TenantSettingsContext).orgConfig?.orgSettings
      ?.patientRecordSettings?.imagingSettings?.enabled ?? false

  const submitModule = (moduleName, moduleValues) => {
    handleChange({
      moduleName: 'assess',
      field: 'modules',
      value: [
        ...(formValues?.assess?.modules ?? []),
        { ...moduleValues, moduleName },
      ],
    })
    setSelectedModule({})
  }

  const items = [
    {
      name: 'Primary Survey',
      module: <PrimarySurvey submitModule={submitModule} />,
      color: 'anRed',
      variant: 'contained',
    },
    {
      name: 'Respiratory',
      module: <Respiratory submitModule={submitModule} />,
    },
    {
      name: 'Cardiovascular',
      module: <Cardiovascular submitModule={submitModule} />,
    },
    { name: 'ECG', module: <ECG submitModule={submitModule} /> },
    { name: 'Neurological', module: <Neuro submitModule={submitModule} /> },
    {
      name: 'Gastrointestinal',
      module: <Gastrointestinal submitModule={submitModule} />,
    },
    {
      name: 'Musculoskeletal',
      module: <Musculoskeletal submitModule={submitModule} />,
    },
    { name: 'Head Injury', module: <HeadInjury submitModule={submitModule} /> },
    { name: 'Eyes', module: <Eyes submitModule={submitModule} /> },
    { name: 'Trauma', module: <Trauma submitModule={submitModule} /> },
    { name: 'Skin', module: <Skin submitModule={submitModule} /> },
    { name: 'ENT', module: <ENT submitModule={submitModule} /> },
    { name: 'Wound', module: <Wound submitModule={submitModule} /> },
    {
      name: 'Mental Capacity',
      module: <MentalCapacity submitModule={submitModule} />,
    },
    {
      name: 'Mental Health',
      module: <MentalHealth submitModule={submitModule} />,
    },
    { name: 'POC', module: <POC submitModule={submitModule} />, color: 'anBlue', variant: 'contained' },
    ...(imagingEnabled
      ? [
          {
            name: 'Imaging',
            color: 'anBlue',
            variant: 'contained',
            module: <Imaging onClose={() => setSelectedModule({})} />,
          },
        ]
      : []),
    {
      name: 'Safeguarding',
      color: 'anPurple',
      variant: 'contained',
      module: <Safeguarding onClose={() => setSelectedModule({})} />,
    },
    {
      name: 'Body Maps',
      color: 'anBlue',
      variant: 'contained',
      module: <BodyMaps submitModule={submitModule} />,
    },
  ]

  const { setToast } = useContext(GlobalContext)

  // Quick "Record Impression" from an Imaging entry - fetches the live
  // request first since the entry is only a summary snapshot
  const openImpression = async (module) => {
    const res = await API.get(`/epcr/${formID}/imaging-requests`)
    const request = (res.data?.requests ?? []).find(
      (x) => x._id === module.imagingRequestId,
    )
    if (res.status !== 200 || !request) {
      setToast({
        message: 'Unable to load the imaging request',
        severity: 'error',
      })
      return
    }
    if (!['Approved', 'Completed'].includes(request.status)) {
      setToast({
        message:
          request.status === 'Requested'
            ? 'This study is still awaiting approval'
            : `This request has been ${request.status.toLowerCase()}`,
        severity: 'warning',
      })
      return
    }
    setImpressionRequest(request)
  }

  // Imaging entries cancel the underlying request (removing it from the
  // approver worklist) instead of just dropping the summary entry
  const cancelImagingRequest = async (module) => {
    const res = await API.delete(
      `/imaging-requests/${module.imagingRequestId}`,
    )
    if (res.status === 200 || res.status === 404) {
      handleChange({
        moduleName: 'assess',
        field: 'modules',
        value: (formValues?.assess?.modules ?? []).filter(
          (x) => x.imagingRequestId !== module.imagingRequestId,
        ),
      })
      setToast({ message: 'Imaging request cancelled' })
    } else {
      setToast({
        message:
          res.data?.errorMessage ?? 'Unable to cancel the imaging request',
        severity: 'error',
      })
    }
  }

  // Newest first, on a copy - sorting formValues in place mutates state
  const modules = [...(formValues?.assess?.modules ?? [])].sort(
    (a, b) => moment(b.dateTime) - moment(a.dateTime),
  )

  return (
    <Stack gap={1}>
      <EPCRCard>
        <Grid container spacing={1}>
          {items.map((x) => (
            <Grid item xs={6} md={4} lg={3} xl={2} key={x.name}>
              <Button
                disabled={!x?.module}
                onClick={() => setSelectedModule(x)}
                variant={
                  modules?.some((m) => m.moduleName === x.name)
                    ? 'contained'
                    : 'outlined'
                }
                size="small"
                color={x?.color ?? 'anMainBlue'}
                fullWidth
              >
                {x.name}
              </Button>
            </Grid>
          ))}
        </Grid>
        {isCriticalCare && (
          <Box mt={1}>
            <ManualInterventionButton
              section="assess"
              onOpen={setDetailRequest}
              fullWidth
            />
          </Box>
        )}
      </EPCRCard>
      <EPCRCard>
        <SimpleTextField
          value={formValues?.assess?.freeText}
          onChange={(e) =>
            handleChange({
              moduleName: 'assess',
              field: 'freeText',
              value: e.target.value,
            })
          }
          multiline
          minRows={3}
          label="Assessment"
        />
      </EPCRCard>
      {formValues?.safeguarding?.concerns && (
        <Grid container component={Paper} padding={1} rowGap={1}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="title3">Safeguarding</Typography>
            </Box>
            <DetailRow
              label="Concerns"
              value={formValues?.safeguarding?.concernsDetails ?? ''}
            />
            <DetailRow
              label="Discussed With"
              value={formValues?.safeguarding?.discussedWith?.join(', ') ?? ''}
            />
            <DetailRow
              label="Referral Made"
              value={formValues?.safeguarding?.referralMade ? 'Yes' : 'No'}
            />
            <DetailRow
              label="Referral Info"
              value={formValues?.safeguarding?.referralInfo ?? ''}
            />
          </Grid>
        </Grid>
      )}
      {modules.map((module, index) => (
          <Grid
            container
            component={Paper}
            padding={1}
            key={module.entryId ?? `${module.dateTime}-${index}`}
            rowGap={1}
          >
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Box display="flex">
                  <Typography variant="title3">{module.moduleName}</Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  flex={1}
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <LiveEntryActions
                    section="assess"
                    entry={module}
                    onOpen={setDetailRequest}
                  />
                  {module.imagingRequestId && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => openImpression(module)}
                    >
                      {module.Assessment
                        ? 'Edit Impression'
                        : 'Record Impression'}
                    </Button>
                  )}
                  <Typography variant="button">
                    {moment(module.dateTime).format('HH:mm')}
                  </Typography>
                  {module.imagingRequestId ? (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => cancelImagingRequest(module)}
                    >
                      Cancel Request
                    </Button>
                  ) : (
                    <DeleteIcon
                      color="error"
                      onClick={async () => {
                        const handleDelete = () =>
                          handleChange({
                            moduleName: 'assess',
                            field: 'modules',
                            value: modules.filter((x, indx) => indx !== index),
                          })
                        if (module.awsMedicalDrawing) {
                          await API.post('/deletes3object', {
                            key: module.awsMedicalDrawing,
                          }).then((res) => {
                            if (res.status === 200) {
                              handleDelete()
                              setToast({ message: 'Successfully Deleted' })
                            } else {
                              setToast({
                                message: 'Unable to delete',
                                severity: 'error',
                              })
                            }
                          })
                        } else handleDelete()
                      }}
                      sx={{ width: '1.5rem', height: '1.5rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              flexWrap="wrap"
              columnGap={5}
              rowGap={1}
            >
              {Object.keys(module)
                .filter(
                  (x) =>
                    !isHiddenEntryKey(x, [
                      'dateTime',
                      'moduleName',
                      'Assessment',
                      'Image',
                      'Notes',
                      'awsMedicalDrawing',
                      'imagingRequestId',
                    ]) && module[x] !== undefined,
                )
                .map((x) => (
                  <Box key={x} display="flex" gap={0.5}>
                    <Typography fontWeight="bold">{x}: </Typography>
                    <Typography variant="body1">
                      {Array.isArray(module[x])
                        ? module[x].join(', ')
                        : module[x]}
                    </Typography>
                  </Box>
                ))}
            </Grid>
            {module?.['awsMedicalDrawing'] && (
              <MedicalDrawing module={module} />
            )}
            {module?.['Notes'] && (
              <Grid item xs={12} lg={6}>
                <Box display="flex" gap={0.5}>
                  <Typography fontWeight="bold">Notes:</Typography>
                  <Typography variant="body1">{module['Notes']}</Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              {module?.['Assessment'] && (
                <Box display="flex" gap={0.5}>
                  <Typography fontWeight="bold">Assessment:</Typography>
                  <Typography variant="body1">
                    {module['Assessment']}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        ))}
      <NextButton newModule="Vitals" />
      <LiveDetailForm
        request={detailRequest}
        onClose={() => setDetailRequest(null)}
      />
      <BigModal
        open={Object.keys(selectedModule).length > 0}
        onClose={() => setSelectedModule({})}
      >
        {selectedModule.module}
      </BigModal>
      <ImpressionDialog
        // Remount per request so field state doesn't leak between studies
        key={impressionRequest?._id ?? 'imaging-impression'}
        request={impressionRequest}
        epcrID={formID}
        onClose={() => setImpressionRequest(null)}
        onUpdated={(updated) => {
          handleChange({
            moduleName: 'assess',
            field: 'modules',
            value: syncImagingEntries(formValues?.assess?.modules, updated),
          })
          setImpressionRequest(null)
        }}
      />
    </Stack>
  )
}

export default Assess
